import { useState, useMemo } from "react";
import {
  createOtherTaskType,
  createOtherTask,
  updateOtherTask,
  deleteOtherTask,
  assignTaskEmployee,
  unassignTaskEmployee,
  attachSharePointToTask,
  unlinkSharePointFromTask,
} from "../../api";
import SharePointFilePicker from "../../components/SharePointFilePicker";
import { formatDate } from '../../utils/DateUtils';
import ConfettiBurst from "./ConfettiBurst";
import OwnerBubble from "./OwnerBubble";
import OwnerPickerModal from "./OwnerPickerModal";
import { MONDAY_COLORS } from "./constants";
import { statusKeyOf } from "./taskFilters";
import { DEFAULT_BOOKKEEPING_TYPE, isBookkeepingTask } from "./bookkeeping";

// ============================================================================
// OTHER TASKS TABLE (for the "Other" tab)
// ============================================================================

const ROW_COLOR = { bg: MONDAY_COLORS.default.bg, text: MONDAY_COLORS.default.text };

export default function OtherTasksTable({
  clients,
  employees,
  statusOptions,
  query,
  filters,
  personFilter,
  clientStatusFilterSet,
  // Data is owned by useTasksData so that remounting this table on a tab
  // switch does not refetch. Mutations still call reload().
  tasks,
  setTasks,
  taskTypes,
  setTaskTypes,
  loading,
  reload,
  // Which tab this table is: "bookkeeping" shows only bookkeeping-type tasks,
  // "other" everything else (see ./bookkeeping). Both views share one task
  // list, so an edit that changes a task's type moves it between the tabs.
  kind = "other",
  // The toolbar's Status filter (see taskFilters.statusKeyOf).
  hiddenStatuses = new Set(),
}) {
  const [savingIds, setSavingIds] = useState(new Set());

  // Brief confirmation banner, mirroring the {type, msg} idiom used by
  // TimeApprovalPage / WeekView elsewhere in this codebase.
  const [banner, setBanner] = useState(null);

  // Sort state — clicking a column header sorts by that field
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // SharePoint picker (lifted into this component so each task gets the real picker)
  const [pickerOpenForTask, setPickerOpenForTask] = useState(null);

  // Notes popover state — { taskId, draft }
  const [notesPopover, setNotesPopover] = useState(null);

  // Notes hover preview state — task whose preview is shown
  const [notesHoverTaskId, setNotesHoverTaskId] = useState(null);

  // Attachments popover — task whose attachments list is open
  const [openAttachmentsTaskId, setOpenAttachmentsTaskId] = useState(null);

  // Assignee picker popover — task whose assignee picker is open
  const [openAssigneeTaskId, setOpenAssigneeTaskId] = useState(null);

  // Confetti state — rendered as a portal when triggered
  const [confettiKey, setConfettiKey] = useState(0);

  // Hover/click-to-edit state, mirroring the Personal/Corporate grid. Cells show
  // plain text until hovered (date fields) or clicked (text/select fields), at
  // which point the editor is revealed. Keyed as `${taskId}-${colKey}`.
  const [hoveredCell, setHoveredCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);

  // Find an existing task type by name, or create a new one if it doesn't
  // exist. Returns the type's ID (or null if name is empty/blank).
  const findOrCreateTaskType = async (typedName) => {
    const name = (typedName || "").trim();
    if (!name) return null;
    const existing = taskTypes.find(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) return existing.id;
    try {
      const created = await createOtherTaskType({ name });
      setTaskTypes((prev) =>
        [...prev, created].sort((a, b) =>
          (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
        )
      );
      return created.id;
    } catch (e) {
      console.error("Failed to create task type", e);
      alert("Failed to create task type: " + (e.message || ""));
      return null;
    }
  };

  // statusOptions is already pre-filtered to "general" statuses by the parent.
  const generalStatuses = useMemo(() => statusOptions || [], [statusOptions]);

  // Resolve a status ID → the full status option (for color, name)
  const statusById = useMemo(() => {
    const m = {};
    for (const s of generalStatuses) m[s.id] = s;
    return m;
  }, [generalStatuses]);

  // Resolve "Not started" / "Completed" status IDs (case-insensitive name match)
  const notStartedStatus = useMemo(
    () => generalStatuses.find((s) => /not\s*started/i.test(s.name)),
    [generalStatuses]
  );
  const completedStatus = useMemo(
    () => generalStatuses.find((s) => /complete/i.test(s.name)),
    [generalStatuses]
  );

  const markSaving = (id, on) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // ----- Inline patch helper -----
  const patchTask = async (id, payload) => {
    markSaving(id, true);
    // Captured before the write so we can tell "just closed" apart from
    // "already closed" below -- the roll-forward only fires on that
    // transition (OtherTaskSerializer._roll_forward_if_recurring).
    const prior = tasks.find((t) => t.id === id);
    try {
      const updated = await updateOtherTask(id, payload);
      if (Object.prototype.hasOwnProperty.call(payload, "status")) {
        // The status dropdown is the only close control for these tasks, and
        // closing a recurring one auto-creates its successor server-side.
        // A merge-only local update never revealed that, so reload. The new
        // occurrence is not-started and future-dated (i.e. "planned"), but
        // the server's active list still includes planned Other tasks due
        // within 60 days, so a monthly/quarterly successor appears here
        // straight away; one due further out stays behind "Include
        // planning".
        await reload();
        const justClosed = prior && !prior.is_completed && updated.is_completed;
        if (justClosed && prior.recurrence && prior.recurrence !== "none") {
          // The list endpoint doesn't serialize rolled_forward_from
          // (OtherTaskSerializer.Meta.fields), so the new row can't be
          // matched up here to name its due date -- a general confirmation
          // is the accurate thing to show rather than a guessed date.
          setBanner({ type: "ok", msg: "Next occurrence created for this recurring task." });
        }
      } else {
        setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated } : t)));
      }
    } catch (e) {
      console.error("Failed to update task", e);
      alert("Failed to update task: " + (e.message || "Unknown error"));
    } finally {
      markSaving(id, false);
    }
  };

  // ----- New-task row state -----
  const [newName, setNewName] = useState("");
  const [newClient, setNewClient] = useState(""); // now holds typed name string
  const defaultNewType = kind === "bookkeeping" ? DEFAULT_BOOKKEEPING_TYPE : "";
  const [newType, setNewType] = useState(defaultNewType);
  const [newDueDate, setNewDueDate] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newRecurrence, setNewRecurrence] = useState("none");

  // Resolve a typed client name to a client ID (case-insensitive). Returns null
  // if the typed value doesn't match any client.
  const resolveClientId = (typedName) => {
    const name = (typedName || "").trim();
    if (!name) return null;
    const match = (clients || []).find(
      (c) => c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    return match ? match.id : null;
  };
  const clientNameById = useMemo(() => {
    const m = {};
    for (const c of clients || []) m[c.id] = c.name;
    return m;
  }, [clients]);

  const handleAddTask = async () => {
    if (!newName.trim()) {
      alert("Task name is required");
      return;
    }
    try {
      const taskTypeId = await findOrCreateTaskType(newType);
      const created = await createOtherTask({
        name: newName.trim(),
        client: resolveClientId(newClient),
        task_type: taskTypeId,
        due_date: newDueDate || null,
        status: newStatus || null,
        recurrence: newRecurrence,
      });
      if (newAssignee) {
        try {
          await assignTaskEmployee(created.id, newAssignee);
        } catch (e) {
          console.error("Assignment failed", e);
        }
      }
      setNewName("");
      setNewClient("");
      setNewType(defaultNewType);
      setNewDueDate("");
      setNewStatus("");
      setNewAssignee("");
      setNewRecurrence("none");
      await reload();
    } catch (e) {
      alert("Failed to create task: " + (e.message || ""));
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await deleteOtherTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) {
      alert("Failed to delete: " + (e.message || ""));
    }
  };

  // ----- Duplicate: clone a row, clear due date, status -> Not started -----
  const handleDuplicate = async (task) => {
    try {
      const created = await createOtherTask({
        name: task.name,
        client: task.client || null,
        task_type: task.task_type || null,
        due_date: null,
        status: notStartedStatus ? notStartedStatus.id : null,
      });
      // Copy assignees over
      const ids = task.assigned_employee_ids || [];
      for (const empId of ids) {
        try {
          await assignTaskEmployee(created.id, empId);
        } catch (e) {
          console.error("Failed to copy assignee", e);
        }
      }
      await reload();
    } catch (e) {
      alert("Failed to duplicate: " + (e.message || ""));
    }
  };

  // ----- Complete: set status to Completed and rain confetti -----
  // Confetti only fires when the status actually transitions to Completed —
  // clicking Complete on an already-completed task does nothing visible.
  const handleComplete = async (task) => {
    if (!completedStatus) {
      alert("No 'Completed' status configured. Add one in settings.");
      return;
    }
    if (String(task.status) === String(completedStatus.id)) {
      // Already completed — no-op, no confetti.
      return;
    }
    try {
      await patchTask(task.id, { status: completedStatus.id });
      setConfettiKey((k) => k + 1);
    } catch (e) {
      // patchTask already alerts on error
    }
  };

  // ----- Assignees: the shared "Assign Owner" pop-up -----
  // The pop-up hands back the whole chosen set; an Other task is assigned and
  // unassigned one person at a time, so apply the difference.
  const [ownerSaving, setOwnerSaving] = useState(false);
  const saveAssignees = async (task, ids) => {
    const before = new Set((task.assigned_employee_ids || []).map(Number));
    const after = new Set(ids.map(Number));
    const toAdd = [...after].filter((id) => !before.has(id));
    const toRemove = [...before].filter((id) => !after.has(id));
    setOwnerSaving(true);
    markSaving(task.id, true);
    try {
      for (const id of toRemove) await unassignTaskEmployee(task.id, id);
      for (const id of toAdd) await assignTaskEmployee(task.id, id);
      setOpenAssigneeTaskId(null);
      await reload();
    } catch (e) {
      alert("Failed to update assignment: " + (e.message || ""));
    } finally {
      setOwnerSaving(false);
      markSaving(task.id, false);
    }
  };

  // ----- SharePoint attach (real picker) -----
  const openPickerFor = (task) => setPickerOpenForTask(task);
  const closePicker = () => setPickerOpenForTask(null);
  const onSharePointFileSelected = async (file) => {
    if (!pickerOpenForTask) return;
    const taskId = pickerOpenForTask.id;
    try {
      await attachSharePointToTask(taskId, {
        name: file.name,
        webUrl: file.webUrl,
        id: file.id || "",
      });
      await reload();
    } catch (e) {
      alert("Failed to attach: " + (e.message || ""));
    } finally {
      closePicker();
    }
  };

  const handleUnlink = async (fileId) => {
    try {
      await unlinkSharePointFromTask(fileId);
      await reload();
    } catch (e) {
      alert("Failed to unlink: " + (e.message || ""));
    }
  };

  // ----- Notes popover -----
  const openNotes = (task) =>
    setNotesPopover({ taskId: task.id, draft: task.notes || "" });
  const closeNotes = () => setNotesPopover(null);
  const saveNotes = async () => {
    if (!notesPopover) return;
    const { taskId, draft } = notesPopover;
    closeNotes();
    await patchTask(taskId, { notes: draft });
  };

  // ----- Sort handler -----
  const onHeaderClick = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Build a quick client lookup for client-scoped filtering (Person fallback,
  // Client status). Recomputed only when the clients list changes.
  const clientById = useMemo(() => {
    const m = {};
    for (const c of clients || []) m[c.id] = c;
    return m;
  }, [clients]);

  // ----- Filter + sort the tasks for display -----
  const visibleTasks = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    let rows = tasks.filter(
      (t) => isBookkeepingTask(t, taskTypes) === (kind === "bookkeeping"));
    if (q) {
      rows = rows.filter((t) => {
        const cName = (clientNameById[t.client] || "").toLowerCase();
        const tName = (t.name || "").toLowerCase();
        const notes = (t.notes || "").toLowerCase();
        return tName.includes(q) || cName.includes(q) || notes.includes(q);
      });
    }

    // The toolbar's Status checklist -- the same keys the Personal and
    // Corporate grid uses, so Completed is hidden here by default too.
    if (hiddenStatuses.size > 0) {
      const finished = (t) =>
        !!t.is_completed || (completedStatus && t.status === completedStatus.id);
      rows = rows.filter((t) => !hiddenStatuses.has(statusKeyOf(t, finished)));
    }

    // Person filter (toolbar). The user picks employee IDs they want to see
    // tasks for. We match against task.assigned_employee_ids first, then
    // fall back to the parent client's assignees if the task itself has
    // none — that's the "Both" semantics: task-level OR client-scoped.
    if (personFilter && personFilter.size > 0) {
      rows = rows.filter((t) => {
        const taskIds = (t.assigned_employee_ids || []).map((id) => String(id));
        if (taskIds.some((id) => personFilter.has(id))) return true;

        // Fallback to the client's owners
        const client = clientById[t.client];
        if (!client) return false;
        const clientIds = [
          ...((client.assigned_employees || []).map((e) =>
            String(typeof e === "object" ? e.id : e)
          )),
          ...(Array.isArray(client.owner)
            ? client.owner.map((o) => String(o))
            : client.owner != null
            ? [String(client.owner)]
            : []),
        ];
        return clientIds.some((id) => personFilter.has(id));
      });
    }

    // Client status filter (Active / Completed / Inactive). Inherits from
    // the parent client; tasks attached to a client whose status is not
    // selected are hidden. Tasks with no resolvable client are kept (they
    // can't be evaluated, and dropping them would surprise the user).
    if (clientStatusFilterSet && clientStatusFilterSet.size > 0) {
      rows = rows.filter((t) => {
        const client = clientById[t.client];
        if (!client) return true;
        const rawStatus = (client.client_status || "").toString().toLowerCase();
        const inferredStatus = rawStatus
          ? rawStatus
          : client.is_active === false
          ? "inactive"
          : "active";
        return clientStatusFilterSet.has(inferredStatus);
      });
    }

    if (!sortField) return rows;
    const cmp = (a, b) => {
      let av, bv;
      switch (sortField) {
        case "name":
          av = (a.name || "").toLowerCase();
          bv = (b.name || "").toLowerCase();
          break;
        case "client":
          av = (clientNameById[a.client] || "").toLowerCase();
          bv = (clientNameById[b.client] || "").toLowerCase();
          break;
        case "type": {
          const at = taskTypes.find((tt) => tt.id === a.task_type);
          const bt = taskTypes.find((tt) => tt.id === b.task_type);
          av = (at?.name || "").toLowerCase();
          bv = (bt?.name || "").toLowerCase();
          break;
        }
        case "due_date":
          av = a.due_date || "";
          bv = b.due_date || "";
          break;
        case "status":
          av = (statusById[a.status]?.name || "").toLowerCase();
          bv = (statusById[b.status]?.name || "").toLowerCase();
          break;
        default:
          av = "";
          bv = "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    };
    return [...rows].sort(cmp);
  }, [
    tasks,
    query,
    sortField,
    sortDir,
    clientNameById,
    taskTypes,
    statusById,
    personFilter,
    clientStatusFilterSet,
    clientById,
    kind,
    hiddenStatuses,
    completedStatus,
  ]);

  // ----- Render styles -----
  // Match the Corporate/Personal grid: flush rows, 12px horizontal padding,
  // thin row separators.
  // Used by the compact add-task row at the top of the table.
  const cellStyle = {
    padding: "4px 12px",
    fontSize: 13,
    borderBottom: "1px solid #e5e7eb",
    verticalAlign: "middle",
  };
  // Header cells mirror the main table: sticky, uppercase, muted gray on the
  // same #f6f7fb band with a 2px bottom rule.
  const headerCellStyle = {
    padding: "0 12px",
    height: 36,
    fontSize: 12,
    fontWeight: 500,
    color: "#676879",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    textAlign: "left",
    whiteSpace: "nowrap",
    background: "#f6f7fb",
    borderBottom: "2px solid #d0d4e4",
    position: "sticky",
    top: 0,
    zIndex: 10,
  };
  const inputStyle = {
    width: "100%",
    padding: "4px 8px",
    border: "1px solid #d0d4e4",
    borderRadius: 4,
    fontSize: 13,
    background: "white",
  };

  // Every row the same quiet band, exactly as the Personal/Corporate grid
  // renders (MONDAY_COLORS.default) -- the status shows as a coloured pill in
  // its own cell, not by shading the whole line. Painting rows in their status
  // colour made this table a wall of colour next to the others.
  const rowColorFor = () => ROW_COLOR;

  // Per-cell style matching the main grid's getCellStyle: flush 12px padding,
  // status-colored background (lightened on hover), ellipsis truncation, 36px row.
  const cellStyleFor = (taskId, colKey, rowColor) => {
    const isHovered = hoveredCell === `${taskId}-${colKey}`;
    return {
      padding: "0 12px",
      height: 36,
      background: isHovered ? "rgba(255,255,255,0.15)" : rowColor.bg,
      transition: "background-color 0.15s ease",
      fontSize: 13,
      color: rowColor.text,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      borderRight: "1px solid rgba(0,0,0,0.06)",
      verticalAlign: "middle",
    };
  };

  // Compact inline editor revealed inside hover/click-to-edit cells.
  const editInputStyle = {
    width: "100%",
    padding: "2px 6px",
    border: "1px solid #0073ea",
    borderRadius: 4,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
    background: "#ffffff",
    color: "#323338",
    boxSizing: "border-box",
  };

  // Sortable header cell
  const SortHeader = ({ field, label, width }) => {
    const active = sortField === field;
    return (
      <th
        onClick={() => onHeaderClick(field)}
        style={{
          ...headerCellStyle,
          width,
          cursor: "pointer",
          userSelect: "none",
        }}
        title="Click to sort"
      >
        {label}
        {active && (
          <span style={{ marginLeft: 6, color: "#0073ea", fontSize: 11 }}>
            {sortDir === "asc" ? "▲" : "▼"}
          </span>
        )}
      </th>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: "#6b7280", textAlign: "center" }}>
        Loading tasks…
      </div>
    );
  }

  const ownerTask = tasks.find((t) => t.id === openAssigneeTaskId) || null;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#ffffff" }}>
      {ownerTask && (
        <OwnerPickerModal
          employees={employees || []}
          initialIds={ownerTask.assigned_employee_ids || []}
          busy={ownerSaving}
          onSave={(ids) => saveAssignees(ownerTask, ids)}
          onClose={() => setOpenAssigneeTaskId(null)}
        />
      )}
      {banner && (
        <div
          style={{
            margin: "10px 16px 0",
            padding: "8px 12px",
            borderRadius: 7,
            fontSize: 13,
            background: banner.type === "ok" ? "#dcfce7" : "#fee2e2",
            color: banner.type === "ok" ? "#166534" : "#991b1b",
          }}
        >
          {banner.msg}
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", background: "#ffffff" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 70 }} />
          <col style={{ width: 220 }} />
        </colgroup>
        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
          <tr>
            <th
              style={{ ...headerCellStyle, width: 44, textAlign: "right", padding: "0 8px" }}
              title="Position in the current view — renumbers when you sort or filter"
            >
              #
            </th>
            <SortHeader field="name" label="Task name" width="20%" />
            <SortHeader field="client" label="Client" width="14%" />
            <SortHeader field="type" label="Type" width="11%" />
            <SortHeader field="due_date" label="Due date" width="10%" />
            <SortHeader field="status" label="Status" width="11%" />
            <th style={{ ...headerCellStyle, width: 110 }} title="Closing a repeating task creates the next one automatically">Repeats</th>
            <th style={{ ...headerCellStyle, width: 130 }}>Assignees</th>
            <th style={{ ...headerCellStyle, width: 130 }}>Attachments</th>
            <th style={{ ...headerCellStyle, width: 70 }}>Notes</th>
            <th style={{ ...headerCellStyle, width: 240 }}></th>
          </tr>
        </thead>
        <tbody>
          {/* New-task row */}
          <tr style={{ background: "#fafbff" }}>
            {/* No ordinal for the new-task row — it is not part of the list */}
            <td style={{ ...cellStyle, padding: "4px 8px" }} />
            <td style={cellStyle}>
              <input
                style={inputStyle}
                placeholder="New task name…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddTask();
                }}
              />
            </td>
            <td style={cellStyle}>
              <input
                style={inputStyle}
                list="other-clients-list"
                placeholder="Search client…"
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
              />
              <datalist id="other-clients-list">
                {(clients || []).map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </td>
            <td style={cellStyle}>
              <input
                style={inputStyle}
                list="other-task-types-list"
                placeholder="Type or pick…"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
              />
              <datalist id="other-task-types-list">
                {taskTypes.map((tt) => (
                  <option key={tt.id} value={tt.name} />
                ))}
              </datalist>
            </td>
            <td style={cellStyle}>
              <input
                type="date"
                style={inputStyle}
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
              />
            </td>
            <td style={cellStyle}>
              <select
                style={inputStyle}
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">—</option>
                {generalStatuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </td>
            <td style={cellStyle}>
              <select
                style={inputStyle}
                value={newRecurrence}
                onChange={(e) => setNewRecurrence(e.target.value)}
              >
                <option value="none">—</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annually</option>
              </select>
            </td>
            <td style={cellStyle}>
              <select
                style={inputStyle}
                value={newAssignee}
                onChange={(e) => setNewAssignee(e.target.value)}
              >
                <option value="">— optional —</option>
                {(employees || [])
                  .filter((e) => e.is_active !== false)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name || e.full_name || e.user?.username}
                    </option>
                  ))}
              </select>
            </td>
            <td style={cellStyle}>
              <span style={{ color: "#9ca3af", fontSize: 12 }}>Add task first</span>
            </td>
            <td style={cellStyle}>
              <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
            </td>
            <td style={cellStyle}>
              <button
                onClick={handleAddTask}
                style={{
                  padding: "6px 12px",
                  background: "#0073ea",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Add
              </button>
            </td>
          </tr>

          {/* Existing rows */}
          {visibleTasks.length === 0 ? (
            <tr>
              <td colSpan={11} style={{ ...cellStyle, textAlign: "center", color: "#9ca3af", padding: 24 }}>
                {tasks.length === 0
                  ? "No tasks yet. Add one above."
                  : kind === "bookkeeping"
                    ? "No bookkeeping tasks match. Add one above."
                    : "No tasks match your search."}
              </td>
            </tr>
          ) : (
            visibleTasks.map((task, rowIdx) => {
              const isSaving = savingIds.has(task.id);
              const status = statusById[task.status];
              const rowColor = rowColorFor(task);
              const clientName = clientNameById[task.client] || "";
              return (
                <tr
                  key={task.id}
                  style={{
                    opacity: isSaving ? 0.6 : 1,
                    height: 36,
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(0.97)")}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
                >
                  {/* Row number — positional, renumbers with the view */}
                  <td
                    style={{
                      padding: "0 8px",
                      height: 36,
                      textAlign: "right",
                      fontSize: 12,
                      fontVariantNumeric: "tabular-nums",
                      color: rowColor.text,
                      opacity: 0.65,
                      background: rowColor.bg,
                      verticalAlign: "middle",
                      borderRight: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    {rowIdx + 1}
                  </td>

                  {/* Task name — click to edit */}
                  <td
                    style={cellStyleFor(task.id, "name", rowColor)}
                    title={task.name || ""}
                    onMouseEnter={() => setHoveredCell(`${task.id}-name`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => {
                      if (editingCell !== `${task.id}-name`) setEditingCell(`${task.id}-name`);
                    }}
                  >
                    {editingCell === `${task.id}-name` ? (
                      <input
                        autoFocus
                        style={editInputStyle}
                        defaultValue={task.name}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          setEditingCell(null);
                          if (v && v !== task.name) patchTask(task.id, { name: v });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") setEditingCell(null);
                        }}
                      />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{task.name || "—"}</span>
                    )}
                  </td>

                  {/* Client — click to edit (datalist) */}
                  <td
                    style={cellStyleFor(task.id, "client", rowColor)}
                    title={clientName}
                    onMouseEnter={() => setHoveredCell(`${task.id}-client`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => {
                      if (editingCell !== `${task.id}-client`) setEditingCell(`${task.id}-client`);
                    }}
                  >
                    {editingCell === `${task.id}-client` ? (
                      <input
                        autoFocus
                        style={editInputStyle}
                        list="other-clients-list"
                        placeholder="Search client…"
                        defaultValue={clientName}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const typed = e.target.value.trim();
                          setEditingCell(null);
                          if (typed === clientName) return;
                          if (!typed) {
                            patchTask(task.id, { client: null });
                            return;
                          }
                          const cid = resolveClientId(typed);
                          if (cid) {
                            patchTask(task.id, { client: cid });
                          } else {
                            alert("No client matches \"" + typed + "\". Pick one from the suggestions.");
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") setEditingCell(null);
                        }}
                      />
                    ) : (
                      <span>{clientName || "—"}</span>
                    )}
                  </td>

                  {/* Type — click to edit (datalist, find-or-create) */}
                  <td
                    style={cellStyleFor(task.id, "type", rowColor)}
                    title={task.task_type_name || ""}
                    onMouseEnter={() => setHoveredCell(`${task.id}-type`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => {
                      if (editingCell !== `${task.id}-type`) setEditingCell(`${task.id}-type`);
                    }}
                  >
                    {editingCell === `${task.id}-type` ? (
                      <input
                        autoFocus
                        style={editInputStyle}
                        list="other-task-types-list"
                        placeholder="Type or pick…"
                        defaultValue={task.task_type_name || ""}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={async (e) => {
                          const typed = e.target.value.trim();
                          const currentName = task.task_type_name || "";
                          setEditingCell(null);
                          if (typed === currentName) return;
                          if (!typed) {
                            patchTask(task.id, { task_type: null });
                            return;
                          }
                          const taskTypeId = await findOrCreateTaskType(typed);
                          if (taskTypeId) {
                            patchTask(task.id, { task_type: taskTypeId });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") setEditingCell(null);
                        }}
                      />
                    ) : (
                      <span>{task.task_type_name || "—"}</span>
                    )}
                  </td>

                  {/* Due date — hover to edit */}
                  <td
                    style={cellStyleFor(task.id, "due_date", rowColor)}
                    onMouseEnter={() => setHoveredCell(`${task.id}-due_date`)}
                    onMouseLeave={() => setHoveredCell(null)}
                  >
                    {hoveredCell === `${task.id}-due_date` ? (
                      <input
                        type="date"
                        autoFocus
                        value={task.due_date || ""}
                        onChange={(e) =>
                          patchTask(task.id, { due_date: e.target.value || null })
                        }
                        onMouseLeave={(e) => e.stopPropagation()}
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          borderRadius: 4,
                          border: "1px solid rgba(255,255,255,0.5)",
                          fontSize: 12,
                          outline: "none",
                          background: "rgba(255,255,255,0.9)",
                          color: "#323338",
                        }}
                      />
                    ) : (
                      <div
                        style={{ cursor: "pointer", fontSize: 12, textAlign: "center" }}
                        onClick={() => setHoveredCell(`${task.id}-due_date`)}
                      >
                        {task.due_date ? formatDate(task.due_date, "MMM DD") : "Set"}
                      </div>
                    )}
                  </td>

                  {/* Status — pill, click to edit */}
                  <td
                    style={{ ...cellStyleFor(task.id, "status", rowColor), overflow: "visible" }}
                    onMouseEnter={() => setHoveredCell(`${task.id}-status`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => {
                      if (editingCell !== `${task.id}-status`) setEditingCell(`${task.id}-status`);
                    }}
                  >
                    {editingCell === `${task.id}-status` ? (
                      <select
                        autoFocus
                        value={task.status || ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setEditingCell(null);
                          patchTask(task.id, { status: e.target.value || null });
                        }}
                        onBlur={() => setEditingCell(null)}
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: 4,
                          fontSize: 13,
                          background: "white",
                          color: "#323338",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">— None —</option>
                        {generalStatuses.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div
                        style={{
                          padding: "4px 8px",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 500,
                          textAlign: "center",
                          borderRadius: 4,
                          background: status?.color || "rgba(255,255,255,0.2)",
                          color: "#ffffff",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {status ? status.name : "Set status"}
                      </div>
                    )}
                  </td>

                  {/* Repeats — closing a recurring task auto-creates the next one server-side */}
                  <td style={cellStyleFor(task.id, "recurrence", rowColor)}>
                    <select
                      value={task.recurrence || "none"}
                      onChange={(e) => patchTask(task.id, { recurrence: e.target.value })}
                      style={{ width: "100%", border: "none", background: "transparent", fontSize: 13 }}
                    >
                      <option value="none">—</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annually</option>
                    </select>
                  </td>
                  <td
                    style={{ ...cellStyleFor(task.id, "assignees", rowColor), overflow: "visible" }}
                    onMouseEnter={() => setHoveredCell(`${task.id}-assignees`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        position: "relative",
                        flexWrap: "nowrap",
                      }}
                    >
                      {(task.assignments || []).slice(0, 3).map((a) => (
                        <OwnerBubble
                          key={a.id}
                          owner={a.employee_name || ""}
                          size={24}
                          onClick={() => setOpenAssigneeTaskId(task.id)}
                        />
                      ))}
                      {(task.assignments || []).length > 3 && (
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "rgba(255,255,255,0.3)",
                            color: rowColor.text,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          +{(task.assignments || []).length - 3}
                        </div>
                      )}
                      <button
                        onClick={() => setOpenAssigneeTaskId(task.id)}
                        aria-label={`Assign owners for ${task.name}`}
                        title="Assign owners"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          border: "2px dashed rgba(255,255,255,0.5)",
                          background: "transparent",
                          color: rowColor.text,
                          fontSize: 14,
                          lineHeight: 1,
                          cursor: "pointer",
                          flexShrink: 0,
                          padding: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td
                    style={{ ...cellStyleFor(task.id, "files", rowColor), overflow: "visible" }}
                    onMouseEnter={() => setHoveredCell(`${task.id}-files`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 4, position: "relative" }}>
                      <button
                        onClick={() => setOpenAttachmentsTaskId(
                          openAttachmentsTaskId === task.id ? null : task.id
                        )}
                        title="View attachments"
                        style={{
                          padding: "4px 8px",
                          background: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: 11,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          color: rowColor.text,
                        }}
                      >
                        📁 {(task.files || []).length}
                      </button>
                      <button
                        onClick={() => openPickerFor(task)}
                        title="Attach from SharePoint"
                        style={{
                          padding: "4px 6px",
                          background: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: 12,
                          color: rowColor.text,
                        }}
                      >
                        📎
                      </button>
                      {openAttachmentsTaskId === task.id && (task.files || []).length > 0 && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            background: "#ffffff",
                            border: "1px solid #d0d4e4",
                            borderRadius: 6,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                            padding: 8,
                            minWidth: 240,
                            maxWidth: 320,
                            zIndex: 200,
                          }}
                        >
                          {(task.files || []).map((f) => (
                            <div
                              key={f.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 0",
                                borderBottom: "1px solid #f3f4f6",
                              }}
                            >
                              <a
                                href={f.sharepoint_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: 12,
                                  color: "#0073ea",
                                  textDecoration: "none",
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={f.name}
                              >
                                📎 {f.name}
                              </a>
                              <button
                                onClick={() => handleUnlink(f.id)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  color: "#9ca3af",
                                  fontSize: 14,
                                  padding: 0,
                                  lineHeight: 1,
                                }}
                                title="Unlink"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td
                    style={{ ...cellStyleFor(task.id, "notes", rowColor), overflow: "visible" }}
                    onMouseEnter={() => setHoveredCell(`${task.id}-notes`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <button
                        onClick={() => openNotes(task)}
                        onMouseEnter={() => {
                          if (task.notes) setNotesHoverTaskId(task.id);
                        }}
                        onMouseLeave={() => setNotesHoverTaskId(null)}
                        title={task.notes ? "Click to edit" : "Click to add notes"}
                        style={{
                          border: "1px solid #d0d4e4",
                          background: task.notes ? "#fffbeb" : "white",
                          cursor: "pointer",
                          fontSize: 14,
                          padding: "4px 8px",
                          borderRadius: 4,
                        }}
                      >
                        📝{task.notes ? " •" : ""}
                      </button>
                      {notesHoverTaskId === task.id && task.notes && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            left: 0,
                            background: "#1f2937",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: 6,
                            fontSize: 12,
                            lineHeight: 1.5,
                            maxWidth: 320,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            zIndex: 300,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            pointerEvents: "none",
                          }}
                        >
                          {task.notes}
                        </div>
                      )}
                    </div>
                  </td>
                  <td
                    style={cellStyleFor(task.id, "actions", rowColor)}
                    onMouseEnter={() => setHoveredCell(`${task.id}-actions`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button
                        onClick={() => handleComplete(task)}
                        title="Mark completed"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          border: "1px solid #00c875",
                          background: "#e8f9f0",
                          color: "#00874f",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#00c875";
                          e.currentTarget.style.color = "#ffffff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#e8f9f0";
                          e.currentTarget.style.color = "#00874f";
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Complete
                      </button>
                      <button
                        onClick={() => handleDuplicate(task)}
                        title="Duplicate this task (clears due date, sets status to Not started)"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          border: "1px solid #d0d4e4",
                          background: "#ffffff",
                          color: "#555",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f6f7fb";
                          e.currentTarget.style.borderColor = "#0073ea";
                          e.currentTarget.style.color = "#0073ea";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                          e.currentTarget.style.borderColor = "#d0d4e4";
                          e.currentTarget.style.color = "#555";
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Duplicate
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        title="Delete"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 26,
                          border: "1px solid transparent",
                          background: "transparent",
                          color: "#9ca3af",
                          cursor: "pointer",
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fef2f2";
                          e.currentTarget.style.color = "#ef4444";
                          e.currentTarget.style.borderColor = "#fee2e2";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "#9ca3af";
                          e.currentTarget.style.borderColor = "transparent";
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {/* SharePoint picker for tasks */}
      {pickerOpenForTask && (
        <SharePointFilePicker
          isOpen={true}
          clientName={clientNameById[pickerOpenForTask.client] || pickerOpenForTask.name}
          onClose={closePicker}
          onFileSelected={onSharePointFileSelected}
        />
      )}

      {/* Notes popover */}
      {notesPopover && (
        <div
          onClick={closeNotes}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: 8,
              padding: 20,
              width: "90%",
              maxWidth: 520,
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600 }}>Notes</h3>
            <textarea
              value={notesPopover.draft}
              onChange={(e) =>
                setNotesPopover({ ...notesPopover, draft: e.target.value })
              }
              autoFocus
              rows={8}
              style={{
                width: "100%",
                padding: 10,
                border: "1px solid #d0d4e4",
                borderRadius: 6,
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box",
              }}
              placeholder="Write a note about this task…"
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                onClick={closeNotes}
                style={{
                  padding: "6px 12px",
                  background: "white",
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveNotes}
                style={{
                  padding: "6px 12px",
                  background: "#0073ea",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confetti — re-mounts on confettiKey change to retrigger animation,
          and auto-unmounts via onDone after the animation finishes so it
          doesn't leave a stray full-screen canvas sitting at zIndex 9999. */}
      {confettiKey > 0 && (
        <ConfettiBurst
          key={confettiKey}
          onDone={() => setConfettiKey(0)}
        />
      )}
    </div>
  );
}
